import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import useExperiment from "../../hooks/useExperiment";
import { EXPERIMENT_KEYS } from "../../services/experiments";

const DEFAULT_CONTEXT = Object.freeze({
  showBbbBranding: true,
  variantKey: "control",
  recordExposure: () => null,
});

const BbbBrandingExperimentContext = createContext(DEFAULT_CONTEXT);

export function BbbBrandingExperimentProvider({ children }) {
  const experiment = useExperiment(
    EXPERIMENT_KEYS.funnelSitewideBbbBrandingV1,
    {
      autoPersist: true,
      autoTrackExposure: false,
      surface: "sitewide_branding",
    },
  );
  const exposureTrackedRef = useRef(false);
  const showBbbBranding = !experiment.isVariant("without_bbb");
  const recordExposure = useCallback(
    (surface = "unknown_surface") => {
      if (
        exposureTrackedRef.current ||
        !experiment.assignment ||
        experiment.isPreviewOverride
      ) {
        return null;
      }
      exposureTrackedRef.current = true;
      return experiment.trackMetric({
        metricKey: "bbb_branding_surface_viewed",
        metricType: "exposure",
        once: {
          scope: "session",
          key: `${EXPERIMENT_KEYS.funnelSitewideBbbBrandingV1}:bbb_branding_surface_viewed`,
        },
        properties: {
          bbbBrandingSurface: String(surface || "unknown_surface"),
        },
      });
    },
    [experiment],
  );
  const value = useMemo(
    () => ({
      showBbbBranding,
      variantKey: experiment.variantKey || "control",
      recordExposure,
    }),
    [experiment.variantKey, recordExposure, showBbbBranding],
  );

  return (
    <BbbBrandingExperimentContext.Provider value={value}>
      {children}
    </BbbBrandingExperimentContext.Provider>
  );
}

export function useBbbBrandingVisibility(
  surface = "unknown_surface",
  eligible = true,
) {
  const context = useContext(BbbBrandingExperimentContext);

  useEffect(() => {
    if (!eligible) return;
    Promise.resolve(context.recordExposure(surface)).catch(() => {});
  }, [context, eligible, surface]);

  return context.showBbbBranding;
}

export function BbbBrandingControl({ children, surface = "unknown_surface" }) {
  const showBbbBranding = useBbbBrandingVisibility(surface);
  return showBbbBranding ? children : null;
}

export const useBbbBrandingExperiment = () =>
  useContext(BbbBrandingExperimentContext);
